import React from 'react';
import { X, Minus, Plus, Trash2 } from 'lucide-react';
import { useCart } from '@/context/CartContext';

const CartDrawer = ({ open, onClose, darkMode = false }) => {
  const { cartItems, removeFromCart, updateQuantity, getCartTotal, clearCart } = useCart();

  if (!open) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-50"
        onClick={onClose}
        data-testid="cart-backdrop"
      />

      {/* Drawer */}
      <div
        className={`fixed right-0 top-0 h-full w-full sm:w-96 z-50 shadow-2xl flex flex-col ${
          darkMode ? 'glass-dark' : 'glass'
        }`}
        data-testid="cart-drawer"
      >
        {/* Header */}
        <div
          className={`flex items-center justify-between p-6 border-b ${
            darkMode ? 'border-gold' : ''
          }`}
          style={!darkMode ? { borderColor: 'rgba(224, 178, 59, 0.2)' } : {}}
        >
          <h2
            className={`text-2xl ${
              darkMode ? 'text-gold' : ''
            }`}
            style={!darkMode ? { color: 'var(--burgundy)' } : {}}
          >
            Your Order
          </h2>
          <button
            onClick={onClose}
            className={`p-2 rounded-lg hover:opacity-80 ${
              darkMode ? 'text-cream' : 'text-charcoal'
            }`}
            data-testid="close-cart-button"
          >
            <X size={24} />
          </button>
        </div>

        {/* Cart Items */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {cartItems.length === 0 ? (
            <div className="text-center py-12">
              <p
                className={`text-lg ${
                  darkMode ? 'text-cream' : ''
                }`}
                style={!darkMode ? { color: 'var(--charcoal)' } : {}}
              >
                Your order is empty
              </p>
            </div>
          ) : (
            cartItems.map((item) => (
              <div
                key={item.cartId}
                className={`rounded-xl p-4 ${
                  darkMode ? 'bg-charcoal bg-opacity-50' : 'bg-white bg-opacity-60'
                }`}
                data-testid={`cart-item-${item.cartId}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <h3
                      className={`font-semibold ${
                        darkMode ? 'text-gold' : ''
                      }`}
                      style={!darkMode ? { color: 'var(--burgundy)' } : {}}
                    >
                      {item.name}
                    </h3>
                    <p
                      className={`text-sm mt-1 ${
                        darkMode ? 'text-cream opacity-70' : ''
                      }`}
                      style={!darkMode ? { color: 'var(--charcoal)', opacity: 0.7 } : {}}
                    >
                      ₹{item.selectedPrice}
                    </p>
                  </div>
                  <button
                    onClick={() => removeFromCart(item.cartId)}
                    className="text-red-600 hover:text-red-700 p-1"
                    data-testid={`remove-item-${item.cartId}`}
                  >
                    <Trash2 size={18} />
                  </button>
                </div>

                <div className="flex items-center gap-3">
                  <button
                    onClick={() => updateQuantity(item.cartId, item.quantity - 1)}
                    className="p-1 rounded hover:bg-gray-200"
                    data-testid={`decrease-quantity-${item.cartId}`}
                  >
                    <Minus size={16} />
                  </button>
                  <span
                    className={`font-semibold ${
                      darkMode ? 'text-cream' : ''
                    }`}
                    style={!darkMode ? { color: 'var(--charcoal)' } : {}}
                    data-testid={`quantity-${item.cartId}`}
                  >
                    {item.quantity}
                  </span>
                  <button
                    onClick={() => updateQuantity(item.cartId, item.quantity + 1)}
                    className="p-1 rounded hover:bg-gray-200"
                    data-testid={`increase-quantity-${item.cartId}`}
                  >
                    <Plus size={16} />
                  </button>
                  <span
                    className={`ml-auto font-semibold ${
                      darkMode ? 'text-gold' : ''
                    }`}
                    style={!darkMode ? { color: 'var(--gold)' } : {}}
                  >
                    ₹{item.selectedPrice * item.quantity}
                  </span>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Footer */}
        {cartItems.length > 0 && (
          <div
            className={`p-6 border-t ${
              darkMode ? 'border-gold' : ''
            }`}
            style={!darkMode ? { borderColor: 'rgba(224, 178, 59, 0.2)' } : {}}
          >
            <div className="flex items-center justify-between mb-4">
              <span
                className={`text-lg font-semibold ${
                  darkMode ? 'text-cream' : ''
                }`}
                style={!darkMode ? { color: 'var(--charcoal)' } : {}}
              >
                Total
              </span>
              <span
                className={`text-2xl font-bold ${
                  darkMode ? 'text-gold' : ''
                }`}
                style={!darkMode ? { color: 'var(--gold)' } : {}}
                data-testid="cart-total"
              >
                ₹{getCartTotal()}
              </span>
            </div>

            <button
              onClick={clearCart}
              className="w-full btn-secondary py-3 justify-center mb-3"
              data-testid="clear-cart-button"
            >
              Clear Order
            </button>

            <p
              className={`text-xs text-center ${
                darkMode ? 'text-cream opacity-60' : ''
              }`}
              style={!darkMode ? { color: 'var(--charcoal)', opacity: 0.6 } : {}}
            >
              This is a view-only order. No orders will be placed.
            </p>
          </div>
        )}
      </div>
    </>
  );
};

export default CartDrawer;